import * as React from 'react'
import { Link } from 'react-router-dom'
import { Navbar } from '@/components/navbar'
import { Button } from '@/components/ui/button'
import { ArrowLeft, ChevronDown, Search } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

const FAQ_DATA = [
  {
    category: 'General',
    questions: [
      {
        q: 'What is Newsne.ws?',
        a: 'Newsne.ws is a premium news aggregation platform that brings you curated news from trusted sources across the United States. We help you stay informed with quality journalism from multiple perspectives.'
      },
      {
        q: 'How does Newsne.ws work?',
        a: 'We aggregate news from various trusted sources and present them in an easy-to-read format. You can browse by topic, source, or location, and customize your feed based on your interests.'
      },
      {
        q: 'Is Newsne.ws free?',
        a: 'We offer both free and premium tiers. Free users can access a limited number of articles, while premium subscribers get unlimited access to all content, advanced features, and an ad-free experience.'
      },
    ]
  },
  {
    category: 'Subscriptions & Pricing',
    questions: [
      {
        q: 'What subscription plans do you offer?',
        a: 'We offer monthly and annual subscription plans. Annual subscribers save 20% compared to monthly billing. Visit our pricing page for current rates and features.'
      },
      {
        q: 'Can I cancel my subscription anytime?',
        a: 'Yes! You can cancel your subscription at any time from your account settings. Your access will continue until the end of your current billing period.'
      },
      {
        q: 'Do you offer refunds?',
        a: 'We offer a 7-day money-back guarantee for new subscribers. If you\'re not satisfied within the first 7 days, contact support for a full refund.'
      },
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover) and PayPal. All payments are processed securely through Stripe.'
      },
    ]
  },
  {
    category: 'Article Purchases',
    questions: [
      {
        q: 'What are article purchases?',
        a: 'Some premium articles can be purchased individually for $0.29 if you don\'t have a subscription. Once purchased, you can read the article anytime.'
      },
      {
        q: 'How do article purchases work?',
        a: 'When you try to read a premium article, you\'ll see a purchase dialog. Click "Purchase" to buy the article for one-time access. The article will be added to your purchased articles list.'
      },
      {
        q: 'Can I get a refund for purchased articles?',
        a: 'Article purchases are generally non-refundable. However, if you experience technical issues preventing you from reading a purchased article, contact support for assistance.'
      },
      {
        q: 'Do purchased articles expire?',
        a: 'No! Once you purchase an article, you have lifetime access to it. You can read it as many times as you want.'
      },
    ]
  },
  {
    category: 'Features',
    questions: [
      {
        q: 'Can I bookmark articles?',
        a: 'Yes! Click the bookmark icon on any article to save it for later. Access your bookmarks from the navigation menu or your dashboard.'
      },
      {
        q: 'How do I filter news by state?',
        a: 'Use the filter sidebar on the home page to select a specific state. This will show you news relevant to that geographic region.'
      },
      {
        q: 'Can I customize my news feed?',
        a: 'Premium subscribers can customize their feed by selecting preferred sources and topics in the dashboard. This helps you see more of what matters to you.'
      },
      {
        q: 'How do I search for articles?',
        a: 'Click the search icon in the navigation bar or visit /search. You can search by keywords, topics, or source names.'
      },
    ]
  },
  {
    category: 'Account & Privacy',
    questions: [
      {
        q: 'How do I create an account?',
        a: 'Click "Sign Up" in the navigation bar, enter your email and create a password. You\'ll receive a verification email to activate your account.'
      },
      {
        q: 'Is my data secure?',
        a: 'Yes! We use industry-standard encryption and security practices. We never sell your personal data to third parties. Read our privacy policy for details.'
      },
      {
        q: 'How do I delete my account?',
        a: 'Go to Dashboard > Account Settings > Delete Account. Note that this action is permanent and cannot be undone.'
      },
      {
        q: 'Can I change my email address?',
        a: 'Yes, you can update your email address in your account settings. You\'ll need to verify the new email address.'
      },
    ]
  },
  {
    category: 'Technical Support',
    questions: [
      {
        q: 'The website is not loading properly. What should I do?',
        a: 'Try clearing your browser cache and cookies, or try a different browser. If the issue persists, contact our support team.'
      },
      {
        q: 'I\'m having trouble logging in. What should I do?',
        a: 'Make sure you\'re using the correct email and password. If you forgot your password, use the "Forgot Password" link on the login page.'
      },
      {
        q: 'How do I report a bug or issue?',
        a: 'Contact our support team through the dashboard or email us at support@newsne.ws with details about the issue.'
      },
      {
        q: 'Do you have a mobile app?',
        a: 'Not yet, but our website is fully responsive and works great on mobile devices. A native app is in development!'
      },
    ]
  },
]

export function FAQPage() {
  const [openIndex, setOpenIndex] = React.useState(null)
  const [searchTerm, setSearchTerm] = React.useState('')
  const [selectedCategory, setSelectedCategory] = React.useState('All')

  const categories = ['All', ...FAQ_DATA.map(cat => cat.category)]

  const filteredFAQs = FAQ_DATA.filter(section => {
    if (selectedCategory !== 'All' && section.category !== selectedCategory) {
      return false
    }
    if (searchTerm) {
      return section.questions.some(q =>
        q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.a.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    return true
  }).map(section => ({
    ...section,
    questions: searchTerm
      ? section.questions.filter(q =>
          q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.a.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : section.questions
  }))

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back button */}
        <Link to="/" className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-medium">Back to Home</span>
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Find answers to common questions about Newsne.ws
          </p>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search FAQs..."
              className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </motion.div>

        {/* Categories */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </motion.div>

        {/* FAQ Sections */}
        <div className="space-y-8">
          {filteredFAQs.map((section, sectionIndex) => (
            <motion.div
              key={section.category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * sectionIndex }}
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                {section.category}
              </h2>
              <div className="space-y-3">
                {section.questions.map((item, index) => {
                  const globalIndex = `${sectionIndex}-${index}`
                  const isOpen = openIndex === globalIndex

                  return (
                    <div
                      key={index}
                      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <span className="font-semibold text-gray-900 dark:text-gray-100 pr-4">
                          {item.q}
                        </span>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-transform ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <div className="px-6 pb-4 text-gray-600 dark:text-gray-400 leading-relaxed">
                              {item.a}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {/* No Results */}
        {filteredFAQs.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              No FAQs found matching your search.
            </p>
            <Button onClick={() => setSearchTerm('')}>
              Clear Search
            </Button>
          </div>
        )}

        {/* Contact Support */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-8 text-center"
        >
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Still have questions?
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Can't find the answer you're looking for? Our support team is here to help.
          </p>
          <Button>
            Contact Support
          </Button>
        </motion.div>
      </div>
    </div>
  )
}
